import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { Load } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

import { toApiError } from './toApiError';

export enum CloneJobStatus {
  Pending = 'pending',
  Processing = 'processing',
  Succeeded = 'succeeded',
  Partial = 'partial',
  Failed = 'failed',
}

export function isFinishedCloneJobStatus(status?: CloneJobStatus): boolean {
  return (
    status === CloneJobStatus.Succeeded ||
    status === CloneJobStatus.Partial ||
    status === CloneJobStatus.Failed
  );
}

export enum CloneJobPhase {
  Queued = 'queued',
  Extracting = 'extracting',
  Creating = 'creating',
  Complete = 'complete',
}

export enum CloneJobItemStatus {
  Created = 'created',
  Skipped = 'skipped',
  Failed = 'failed',
}

export enum CloneJobItemReason {
  DuplicateInRequest = 'duplicate_in_request',
  DuplicateExisting = 'duplicate_existing',
  DuplicateInProgress = 'duplicate_in_progress',
  MissingReference = 'missing_reference',
  InvalidRow = 'invalid_row',
  GuardrailRejected = 'guardrail_rejected',
  DuplicateCheckFailed = 'duplicate_check_failed',
  ReservationFailed = 'reservation_failed',
  ReservationLost = 'reservation_lost',
  AmbiguousWrite = 'ambiguous_write',
  TMSCreateFailed = 'tms_create_failed',
  JobFailedBeforeCreate = 'job_failed_before_create',
}

export type CloneJobResultLoad = {
  id?: number;
  freightTrackingId?: string;
  externalTmsId?: string;
  url?: string;
};

export type CloneJobItem = {
  reference: string;
  status: CloneJobItemStatus;
  reason?: CloneJobItemReason;
  message?: string;
  load?: CloneJobResultLoad;
};

export type CloneCreateResponse = {
  jobId: string;
  status: CloneJobStatus;
  phase: CloneJobPhase;
  progress: {
    completed: number;
    total: Maybe<number>;
  };
  summary: {
    created: number;
    skipped: number;
    failed: number;
  };
  items: CloneJobItem[];
  extractionWarnings: string[];
  createdAt: string;
  updatedAt: string;
  completedAt: Maybe<string>;
  createdLoads: Load[];
  errors?: string[];
  warnings?: string[];
  message: string;
};

export interface CloneCreateRequest {
  sourceLoadId: number;
  emailId: number;
  idempotencyKey: string;
  acknowledgeProdTMSOverride?: boolean;
  recommendationId?: number;
  recommendationVersion?: number;
}

export enum LoadCloneRecommendationDisposition {
  Pending = 'pending',
  Dismissed = 'dismissed',
  BuilderChosen = 'builder_chosen',
  Started = 'started',
}

export type LoadCloneRecommendationSource = {
  loadId: number;
  identifier: string;
  customerName: string;
  pickup: string;
  delivery: string;
  equipment: string;
  pickupDate: Maybe<string>;
  deliveryDate: Maybe<string>;
};

export type LoadCloneRecommendation = {
  id: number;
  emailId: number;
  threadId: string;
  version: number;
  disposition: LoadCloneRecommendationDisposition;
  source: Maybe<LoadCloneRecommendationSource>;
  customerName: Maybe<string>;
  reason: Maybe<string>;
  latestJobId: Maybe<string>;
};

export type LoadCloneRecommendationPatch = {
  version: number;
  sourceLoadId?: number;
  disposition?:
    | LoadCloneRecommendationDisposition.Dismissed
    | LoadCloneRecommendationDisposition.BuilderChosen;
};

export async function getLoadCloneRecommendations(
  threadId: string
): Promise<Result<LoadCloneRecommendation[], ApiError>> {
  try {
    const response = await axios.get<{
      recommendations: LoadCloneRecommendation[];
    }>('/load/clone/recommendations', { params: { threadId } });
    return ok(response.data.recommendations);
  } catch (error) {
    return err(toApiError(error, 'Unable to load clone recommendations.'));
  }
}

export async function getLoadCloneRecommendationSources(
  recommendationId: number,
  loadNumber: string
): Promise<Result<LoadCloneRecommendationSource[], ApiError>> {
  try {
    const response = await axios.get<{
      sources: LoadCloneRecommendationSource[];
    }>(`/load/clone/recommendations/${recommendationId}/sources`, {
      params: { loadNumber },
    });
    return ok(response.data.sources);
  } catch (error) {
    return err(toApiError(error, 'Unable to find that load.'));
  }
}

export async function updateLoadCloneRecommendation(
  recommendationId: number,
  patch: LoadCloneRecommendationPatch
): Promise<Result<LoadCloneRecommendation, ApiError>> {
  try {
    const response = await axios.patch<LoadCloneRecommendation>(
      `/load/clone/recommendations/${recommendationId}`,
      patch
    );
    return ok(response.data);
  } catch (error) {
    return err(
      toApiError(
        error,
        'Unable to update this recommendation.',
        isAxiosError(error) ? error.response?.status : undefined
      )
    );
  }
}

export enum ClonePromptGroup {
  Customer = 'customer',
}

export type ClonePromptEntry = {
  key: string;
  group: ClonePromptGroup;
  label: string;
  text: string;
  readOnly: boolean;
};

export type CloneSavedInstructions = {
  revision: string;
  entries: ClonePromptEntry[];
};

export async function saveLoadCloneInstructions(
  context: Pick<CloneCreateRequest, 'sourceLoadId' | 'emailId'>,
  snapshot: CloneSavedInstructions
): Promise<Result<CloneSavedInstructions, ApiError>> {
  try {
    const response = await axios.put<CloneSavedInstructions>(
      '/load/clone/instructions',
      {
        ...context,
        revision: snapshot.revision,
        edits: snapshot.entries.map(({ key, text }) => ({ key, text })),
      }
    );
    return ok(response.data);
  } catch (error) {
    return err(toApiError(error, 'Unable to save instructions.'));
  }
}

export async function getLoadCloneInstructions(
  context: Pick<CloneCreateRequest, 'sourceLoadId' | 'emailId'>
): Promise<Result<CloneSavedInstructions, ApiError>> {
  try {
    const response = await axios.get<CloneSavedInstructions>(
      '/load/clone/instructions',
      { params: context }
    );
    return ok(response.data);
  } catch (error) {
    return err(toApiError(error, 'Unable to load saved instructions.'));
  }
}

export async function createLoadClones(
  context: CloneCreateRequest
): Promise<Result<CloneCreateResponse, ApiError>> {
  try {
    const response = await axios.post<CloneCreateResponse>(
      '/load/clone',
      context
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'cloneLoads' });
    if (isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }
    if (
      error instanceof Error &&
      error.message === 'Extension context invalidated.'
    ) {
      throw error;
    }
    return err(
      toApiError(
        error,
        'Unable to create cloned loads.',
        isAxiosError(error) ? error.response?.status : undefined
      )
    );
  }
}

export async function getLatestLoadCloneJob(
  context: Pick<CloneCreateRequest, 'sourceLoadId' | 'emailId'>
): Promise<Result<Maybe<CloneCreateResponse>, ApiError>> {
  try {
    const response = await axios.get<CloneCreateResponse>('/load/clone', {
      params: context,
    });
    return ok(response.data);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return ok(null);
    }
    captureException(error, { functionName: 'getLatestLoadCloneJob' });
    if (isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }
    if (
      error instanceof Error &&
      error.message === 'Extension context invalidated.'
    ) {
      throw error;
    }
    return err(
      toApiError(
        error,
        'Unable to check load cloning status.',
        isAxiosError(error) ? error.response?.status : undefined
      )
    );
  }
}

export async function getLoadCloneJob(
  jobId: string
): Promise<Result<CloneCreateResponse, ApiError>> {
  try {
    const response = await axios.get<CloneCreateResponse>(
      `/load/clone/${jobId}`
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'getLoadCloneJob' });
    if (isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }
    if (
      error instanceof Error &&
      error.message === 'Extension context invalidated.'
    ) {
      throw error;
    }
    return err(
      toApiError(
        error,
        'Unable to check load cloning progress.',
        isAxiosError(error) ? error.response?.status : undefined
      )
    );
  }
}
